import type { Deal, DealStage } from "@/lib/types/database/deals";
import { ACTIVE_STAGES, isLostStage, isWonDeal } from "@/lib/constants/deal-stages";
import { type QualityRuleDefinition } from "../types";

const today = new Date();
today.setHours(0, 0, 0, 0);

const baseExtract = (deal: Deal) => ({
  id: deal.id,
  title: deal.title,
  company_name: deal.company_name,
  revenue: deal.revenue,
  stage: deal.stage,
  close_date: deal.close_date,
  close_probability: deal.close_probability,
  assigned_users: deal.assigned_users,
});

export const qualityRules: QualityRuleDefinition[] = [
  {
    id: "revenue_without_won",
    name: "Revenue Without Won Status",
    description: "Deals with revenue recorded but not marked as won.",
    severity: "critical",
    suggestedAction: "Mark as Won or adjust stage",
    check: (deal) => deal.revenue > 0 && !isWonDeal(deal.stage as DealStage) && !isLostStage(deal.stage as DealStage) && !deal.won_at,
    extractData: baseExtract,
  },
  {
    id: "stage_mismatch_contract_no_revenue",
    name: "Contract Stage Without Revenue",
    description: "Deals in contract stage should have revenue recorded.",
    severity: "critical",
    suggestedAction: "Add revenue or move to earlier stage",
    check: (deal) => deal.stage === "contract_signed" && deal.revenue === 0,
    extractData: baseExtract,
  },
  {
    id: "missing_forecast_data",
    name: "Missing Forecast Data",
    description: "Active deals missing close date or probability.",
    severity: "warning",
    suggestedAction: "Add close date and probability estimate",
    check: (deal) => ACTIVE_STAGES.includes(deal.stage) && (!deal.close_date || deal.close_probability === null),
    extractData: baseExtract,
  },
  {
    id: "overdue_deals",
    name: "Overdue Deals",
    description: "Active deals with close dates in the past.",
    severity: "critical",
    suggestedAction: "Update close date or mark as won/lost",
    check: (deal) => {
      if (!ACTIVE_STAGES.includes(deal.stage) || !deal.close_date) return false;
      const closeDate = new Date(deal.close_date);
      closeDate.setHours(0, 0, 0, 0);
      return closeDate < today;
    },
    extractData: baseExtract,
  },
  {
    id: "unassigned_deals",
    name: "Unassigned Deals",
    description: "Deals with no assigned owners.",
    severity: "warning",
    suggestedAction: "Assign deal owner(s)",
    check: (deal) => !deal.assigned_users || deal.assigned_users.length === 0,
    extractData: baseExtract,
  },
  {
    id: "high_value_at_risk",
    name: "High-Value At-Risk Deals",
    description: "High-value deals (>$50k) with low win probability (<30%).",
    severity: "critical",
    suggestedAction: "Review strategy or adjust probability",
    check: (deal) => deal.revenue > 50000 && deal.close_probability !== null && deal.close_probability < 30 && ACTIVE_STAGES.includes(deal.stage),
    extractData: baseExtract,
  },
  {
    id: "health_status_red",
    name: "Project Health Issues",
    description: "Deals with red health status flags.",
    severity: "warning",
    suggestedAction: "Review and address health issues",
    check: (deal) => deal.schedule_status === "red" || deal.budget_status === "red" || deal.scope_status === "red" || deal.alignment_status === "red",
    extractData: baseExtract,
  },
  {
    id: "negative_margin",
    name: "Negative Margin Deals",
    description: "Deals where cost exceeds revenue.",
    severity: "critical",
    suggestedAction: "Review pricing or reduce scope",
    check: (deal) => deal.cost !== null && deal.cost !== undefined && deal.revenue > 0 && deal.cost > deal.revenue,
    extractData: baseExtract,
  },
];
